/**
 * Three.js 3D Candle Preview
 * Creates a 3D cylinder with label texture for realistic candle preview
 */

const CONSOLE_LOGGING_ENABLED = false;
// Local console override to silence logging in production.
const silentConsole = {
	log() {},
	warn() {},
	error() {},
};
const console = CONSOLE_LOGGING_ENABLED
	? globalThis.console || silentConsole
	: silentConsole;

class ThreeDCandlePreview {
	constructor(containerElement, canvasElement) {
		this.container = containerElement;
		this.sourceCanvas = canvasElement; // Front canvas
		this.backCanvas = null; // Will be set later
		this.scene = null;
		this.camera = null;
		this.renderer = null;
		this.cylinder = null;
		this.backgroundPlane = null; // Background image plane
		this.controls = null;
		this.animationId = null;
		this.typeColor = "#000000"; // Default type color
		this.backgroundTexture = null; // Background image texture
		this.lights = []; // Store light references to keep them fixed
		this.objectRotationY = 0; // Track object rotation (for fixed camera setup)
		this.initialContainerSize = { width: 0, height: 0 }; // Store initial container size for scaling
		this.initialFrustumSize = { width: 0, height: 0 }; // Store initial frustum size for accurate scaling
		this.initialRotationY = 0; // Store initial rotation for "Front" button
		this.isAnimating = false; // Track if rotation animation is in progress

		// Configuration
		this.config = {
			cylinderRadius: 3.625, // Adjusted to match the candle in the background image
			cylinderHeight: 5.5, // Reduced height for shorter cylinder
			cylinderSegments: 32,
			cameraDistance: 21, // Increased to 22 for better perspective alignment
			cameraYOffset: 2.5, // Raise camera on Y axis
			cylinderYOffset: 2.125, // Vertical offset for label (cylinder) - reduced to move down
			cylinderXOffset: 0, // Horizontal offset for cylinder/label - split difference between -0.075 and 0.15
			sceneYOffset: 1.35, // Lower the whole scene by this many units
			initialLabelRotationDeg: 0, // Not used - rotation is calculated dynamically to center front label
			autoRotate: false, // Paused auto rotation
			autoRotateSpeed: 0.5,
		};

		console.log("🎨 ThreeDCandlePreview constructor called");
		console.log("🎨 Container:", this.container);
		console.log("🎨 Source canvas:", this.sourceCanvas);

		// Get the back canvas
		this.backCanvas = document.getElementById("canvas-back");
		console.log("🎨 Back canvas:", this.backCanvas);
	}

	/**
	 * Initialize the 3D scene
	 */
	async init() {
		console.log("🎨 Initializing 3D preview...");

		// Ensure container height is set to fill viewport minus header
		this.updateContainerHeight();

		// Store initial container size for scaling calculations
		this.initialContainerSize = {
			width: this.container.clientWidth || 1,
			height: this.container.clientHeight || 1,
		};
		console.log("📐 Initial container size:", this.initialContainerSize);

		// Store initial frustum size for accurate scaling with background image
		// Calculate frustum dimensions for camera setup
		const initAspect =
			this.initialContainerSize.width / this.initialContainerSize.height;
		const prevFov = 40; // match previous perspective FOV
		const frustumHeightInit =
			2 *
			this.config.cameraDistance *
			Math.tan(THREE.MathUtils.degToRad(prevFov / 2));
		const frustumWidthInit = frustumHeightInit * initAspect;

		this.initialFrustumSize = {
			width: frustumWidthInit,
			height: frustumHeightInit,
		};
		console.log("📐 Initial frustum size:", this.initialFrustumSize);

		// Store initial background plane size - will be set when background plane is created
		this.initialBackgroundPlaneSize = null;

		// Create scene
		this.scene = new THREE.Scene();
		this.scene.background = null; // Keep null - we use a background plane with object-fit: cover
		console.log("✅ Scene created");

		// Create camera (Orthographic derived from previous FOV and distance)
		// Reuse initAspect, prevFov, frustumHeightInit, and frustumWidthInit already calculated above
		this.camera = new THREE.OrthographicCamera(
			-frustumWidthInit / 2,
			frustumWidthInit / 2,
			frustumHeightInit / 2,
			-frustumHeightInit / 2,
			0.1,
			1000
		);
		this.camera.position.set(
			0,
			this.config.cameraYOffset,
			this.config.cameraDistance
		); // Raise camera vertically
		this.camera.lookAt(0, 0, 0); // Look at center where text will be visible
		console.log("✅ Camera created at position:", this.camera.position);

		// Create renderer
		console.log("🎨 Creating WebGLRenderer...");
		this.renderer = new THREE.WebGLRenderer({
			antialias: true,
			alpha: true, // Enable transparency
		});
		this.renderer.setSize(
			this.container.clientWidth,
			this.container.clientHeight
		);
		this.renderer.setPixelRatio(Math.max(window.devicePixelRatio || 1, 2));
		this.renderer.shadowMap.enabled = true;
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

		// Enable clipping planes for hiding back half of cylinder
		this.renderer.localClippingEnabled = true;

		// Fix 1: Color Space Management
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;

		// Fix 2: Professional Tone Mapping
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 1.275; // Balanced to brighten colors without washing out

		console.log("✅ Renderer created");

		// Clear container and add renderer
		console.log("🎨 Adding renderer to DOM...");
		this.container.innerHTML = "";
		// Ensure container has position relative for absolute positioning
		if (window.getComputedStyle(this.container).position === "static") {
			this.container.style.position = "relative";
		}
		this.container.appendChild(this.renderer.domElement);
		console.log("✅ Renderer added to container");

		// Load background image
		console.log("🎨 Loading background image...");
		await this.loadBackgroundImage();
		console.log(
			"✅ Background image loaded (using scene.background, not plane)"
		);

		// Add lights
		console.log("🎨 Setting up lights...");
		this.setupLights();
		console.log("✅ Lights added");

		// Create cylinder
		console.log("🎨 Creating cylinder...");
		await this.createCylinder();
		console.log("✅ Cylinder created");

		// Add controls
		console.log("🎨 Setting up controls...");
		this.setupControls();
		console.log("✅ Controls added");

		// Create position buttons
		console.log("🎨 Creating position buttons...");
		this.createPositionButtons();
		console.log("✅ Position buttons created");

		// Start animation
		console.log("🎨 Starting animation...");
		this.animate();
		console.log("✅ Animation started");

		// Handle window resize - define handler inline
		window.addEventListener("resize", () => {
			if (!this.container || !this.camera || !this.renderer) return;

			// Update container height to fill viewport minus header
			this.updateContainerHeight();

			const width = this.container.clientWidth;
			const height = this.container.clientHeight;

			console.log("🔄 Window resized to:", width, "x", height);

			// Update orthographic frustum based on container size
			const aspectNew = width / height;
			const frustumHeight =
				2 *
				this.config.cameraDistance *
				Math.tan(THREE.MathUtils.degToRad(40 / 2));
			const frustumWidth = frustumHeight * aspectNew;
			this.camera.left = -frustumWidth / 2;
			this.camera.right = frustumWidth / 2;
			this.camera.top = frustumHeight / 2;
			this.camera.bottom = -frustumHeight / 2;
			this.camera.updateProjectionMatrix();

			this.renderer.setSize(width, height);

			// Update cylinder scale to match background image scaling
			if (this.cylinder) {
				this.updateCylinderScale();
			}

			// Update background plane to maintain object-fit: cover
			if (this.backgroundPlane && this.backgroundTexture) {
				this.createBackgroundPlane();
			}
		});
		console.log("✅ Event listeners added");

		console.log("🎉 3D preview initialization complete!");
	}

	/**
	 * Update container height to fill viewport minus fixed header
	 * Enforces minimum height based on background image aspect ratio to prevent drift
	 * For sm and md viewports (up to 991.98px), enforces 4:3 aspect ratio
	 */
	updateContainerHeight() {
		if (!this.container) return;

		// Check if we're in sm/md viewport range (up to 991.98px)
		const viewportWidth =
			window.innerWidth || document.documentElement.clientWidth;
		const isSmOrMd = viewportWidth <= 991.98;

		if (isSmOrMd) {
			// For sm and md viewports, enforce 4:3 aspect ratio
			const containerWidth = this.container.clientWidth || viewportWidth;
			const aspectRatio = 4 / 3; // 4:3 aspect ratio
			const calculatedHeight = containerWidth / aspectRatio;

			this.container.style.height = `${calculatedHeight}px`;
			console.log(
				"📐 Container height updated to 4:3 aspect ratio:",
				`${calculatedHeight}px (width: ${containerWidth}px)`
			);
			return;
		}

		// For larger viewports, use the original logic
		// Fixed header height of 64px
		const headerHeight = 64;

		// Calculate viewport-based height
		const viewportHeight = `calc(100vh - ${headerHeight}px)`;

		// If background image is loaded, calculate minimum height based on its aspect ratio
		let minHeight = null;
		if (this.backgroundTexture && this.backgroundTexture.image) {
			const containerWidth = this.container.clientWidth || window.innerWidth;
			const imageAspectRatio =
				this.backgroundTexture.image.width /
				this.backgroundTexture.image.height;

			// Calculate minimum height to maintain image aspect ratio
			// This prevents the container from getting too short on wide viewports
			const calculatedMinHeight = containerWidth / imageAspectRatio;

			// Convert to pixels (container width is in pixels)
			minHeight = `${calculatedMinHeight}px`;

			console.log(
				"📐 Container width:",
				containerWidth,
				"Image aspect:",
				imageAspectRatio.toFixed(3),
				"Min height:",
				minHeight
			);
		}

		// Use max() to ensure container is at least as tall as the minimum,
		// but still respects viewport height when it's larger
		if (minHeight) {
			this.container.style.height = `max(${viewportHeight}, ${minHeight})`;
			console.log(
				"📐 Container height updated to:",
				`max(${viewportHeight}, ${minHeight})`
			);
		} else {
			this.container.style.height = viewportHeight;
			console.log(
				"📐 Container height updated to:",
				viewportHeight,
				"(no min height - background not loaded yet)"
			);
		}
	}

	/**
	 * Setup scene lighting
	 * Lights are fixed in world space and don't rotate with camera
	 */
	setupLights() {
		// Balanced ambient light - split difference to brighten without washing out
		const ambientLight = new THREE.AmbientLight(0xffffff, 0.55);
		this.scene.add(ambientLight);
		this.lights.push(ambientLight);

		// Directional light fixed in world space - position represents direction
		// This light will stay fixed regardless of camera rotation or panning
		// Balanced intensity to brighten label colors without washing out
		const frontLight = new THREE.DirectionalLight(0xffffff, 1.2);
		// Fixed world-space position - this light direction won't rotate with camera
		frontLight.position.set(0, 2, 5);
		frontLight.castShadow = false;
		// Lights added directly to scene are automatically in world space
		// They are not children of camera or any rotating object
		this.scene.add(frontLight);
		this.lights.push(frontLight);

		// Store light reference to ensure it stays fixed
		this.frontLight = frontLight;

		console.log(
			"✅ Lights set up in fixed world positions (won't rotate on pan)"
		);
	}

	/**
	 * Load the background image and create a plane to display it
	 */
	async loadBackgroundImage() {
		return new Promise((resolve) => {
			const loader = new THREE.TextureLoader();

			// Get background image URL from global variable set in Liquid template
			// Fallback to direct asset path if variable not set
			const imageUrl =
				typeof window.customCandleBackgroundImageUrl !== "undefined"
					? window.customCandleBackgroundImageUrl
					: "/assets/CustomCandle.jpg";

			console.log("🎨 Loading background image from:", imageUrl);

			loader.load(
				imageUrl,
				(texture) => {
					console.log("✅ Background image loaded successfully");
					console.log(
						"✅ Image dimensions:",
						texture.image.width,
						"x",
						texture.image.height
					);
					console.log("✅ Image source:", texture.image.src);

					// Configure texture properties for centered display
					texture.colorSpace = THREE.SRGBColorSpace;
					texture.wrapS = THREE.ClampToEdgeWrapping;
					texture.wrapT = THREE.ClampToEdgeWrapping;
					// Center the texture on the plane
					texture.center.set(0.5, 0.5);
					texture.offset.set(0, 0);

					this.backgroundTexture = texture;

					// Update container height now that we have the image dimensions
					// This ensures minimum height is calculated based on image aspect ratio
					this.updateContainerHeight();

					// Don't use scene.background as it doesn't support object-fit: cover
					// Instead, create a background plane with proper scaling
					this.scene.background = null;

					// Create background plane with object-fit: cover behavior
					this.createBackgroundPlane();
					console.log("✅ Background plane created with object-fit: cover");

					resolve();
				},
				undefined, // Progress callback
				(error) => {
					console.warn("⚠️ Failed to load background image:", error);
					console.log(
						"🎨 Continuing without background image - 3D preview will still work"
					);
					// Continue without background image - 3D preview will still work
					resolve();
				}
			);
		});
	}

	/**
	 * Create background plane with object-fit: cover behavior
	 * The plane is positioned to always cover the entire viewport without skewing
	 */
	createBackgroundPlane() {
		if (!this.backgroundTexture) {
			console.log("❌ No background texture available");
			return;
		}

		console.log("🎨 Creating background plane with object-fit: cover...");
		console.log(
			"📐 Container dimensions:",
			this.container.clientWidth,
			"x",
			this.container.clientHeight
		);

		// Calculate dimensions based on orthographic camera frustum and image aspect ratio
		// Object-fit: cover means scale to cover, maintain aspect ratio, center and crop
		const containerAspect =
			this.container.clientWidth / this.container.clientHeight;
		const imageAspect =
			this.backgroundTexture.image.width / this.backgroundTexture.image.height;

		console.log("📐 Container aspect:", containerAspect);
		console.log("📐 Image aspect:", imageAspect);

		// Get current orthographic camera frustum dimensions
		// For orthographic camera, the frustum size is the viewport size in world units
		const frustumHeight =
			2 *
			this.config.cameraDistance *
			Math.tan(THREE.MathUtils.degToRad(40 / 2));
		const frustumWidth = frustumHeight * containerAspect;

		console.log("📐 Camera frustum:", frustumWidth, "x", frustumHeight);

		let planeWidth, planeHeight;

		// Object-fit: cover logic - scale to cover the entire frustum
		// The plane must be large enough to cover the viewport while maintaining aspect ratio
		// Use a larger margin to ensure complete coverage, especially at the bottom
		// Increased margin to account for the 2.5 unit lift
		const coverageMargin = 1.2; // 20% larger to guarantee coverage after lifting by 2.5 units
		if (containerAspect > imageAspect) {
			// Container is wider than image - scale to cover width (crop height)
			planeWidth = frustumWidth * coverageMargin;
			planeHeight = (frustumWidth * coverageMargin) / imageAspect;
		} else {
			// Container is taller than image - scale to cover height (crop width)
			planeHeight = frustumHeight * coverageMargin;
			planeWidth = frustumHeight * coverageMargin * imageAspect;
		}

		console.log("📐 Plane dimensions (cover):", planeWidth, "x", planeHeight);

		// Store initial background plane size for scaling calculations
		// This ensures the cylinder scales exactly with the background plane
		if (!this.initialBackgroundPlaneSize) {
			this.initialBackgroundPlaneSize = {
				width: planeWidth,
				height: planeHeight,
			};
			console.log(
				"📐 Initial background plane size stored:",
				this.initialBackgroundPlaneSize
			);
		}

		const planeZ = -this.config.cameraDistance + 0.1;

		// Calculate Y offset to ensure the background plane covers the entire frustum
		// For object-fit: cover, the plane must completely fill the frustum from top to bottom
		// The orthographic camera frustum extends from -frustumHeight/2 (bottom) to +frustumHeight/2 (top)
		// The camera looks at (0, 0, 0), so the frustum is centered at Y=0
		// The plane extends from -planeHeight/2 (bottom) to +planeHeight/2 (top) when centered at Y=0
		//
		// Position the plane to ensure full coverage, especially at the bottom
		// The camera has a Y offset (2.5) which shifts the view upward, creating a gap at the bottom
		// We need to shift the plane down to compensate for the camera Y offset
		// With the larger coverage margin (20%), the plane is significantly larger than frustum
		let planeY = 0;
		if (planeHeight > frustumHeight) {
			// Plane is taller than frustum - shift down to fill bottom gap
			const excessHeight = planeHeight - frustumHeight;
			// Shift down by more of the excess to ensure bottom coverage after lifting
			// Increased from 60% to 75% to account for the 2.5 unit lift
			planeY = -excessHeight * 0.75;
		}
		// Compensate for camera Y offset - shift plane down by most of the camera offset amount
		// Using 90% to leave a small margin at top while filling bottom completely
		planeY -= this.config.cameraYOffset * 0.9;

		// Lift the background image by 3.5 units
		// After lifting, we need to ensure the bottom still covers - adjust positioning
		planeY += 3.5;

		// Additional shift down to ensure bottom coverage after lifting
		// The lift moves the plane up, so we need to compensate to keep bottom covered
		planeY -= 1.0; // Partial compensation to maintain bottom coverage

		console.log(
			"📐 Background plane Y position:",
			planeY,
			"(frustum height:",
			frustumHeight,
			", plane height:",
			planeHeight,
			")"
		);

		// Remove old plane if it exists
		if (this.backgroundPlane) {
			this.scene.remove(this.backgroundPlane);
			this.backgroundPlane.geometry.dispose();
			this.backgroundPlane.material.dispose();
		}

		// Create plane geometry
		const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);

		// Ensure texture is centered (should already be set, but ensure it)
		if (this.backgroundTexture) {
			this.backgroundTexture.center.set(0.5, 0.5);
			this.backgroundTexture.offset.set(0, 0);
		}

		// Create material
		const material = new THREE.MeshBasicMaterial({
			map: this.backgroundTexture,
			side: THREE.DoubleSide,
			depthWrite: false, // Don't write to depth buffer
		});

		// Create mesh
		this.backgroundPlane = new THREE.Mesh(geometry, material);

		// Position plane behind everything, centered at camera look-at point (0, 0, 0)
		// The plane Y position should match the camera's view center
		// With orthographic camera, the view center is at the look-at point, not the camera position
		this.backgroundPlane.position.set(0, planeY, planeZ);
		console.log("📍 Background plane position:", this.backgroundPlane.position);

		// Add to scene (behind everything)
		this.scene.add(this.backgroundPlane);

		// Ensure it renders behind other objects
		this.backgroundPlane.renderOrder = -1;

		console.log("✅ Background plane added to scene with object-fit: cover");
	}

	/**
	 * Update background plane on resize
	 */
	updateBackgroundPlane() {
		if (!this.backgroundPlane || !this.backgroundTexture) {
			console.log(
				"❌ Cannot update background plane - missing plane or texture"
			);
			return;
		}

		console.log("🔄 Updating background plane...");

		// Calculate new dimensions (apply backgroundScale)
		const containerAspect =
			this.container.clientWidth / this.container.clientHeight;
		const imageAspect =
			this.backgroundTexture.image.width / this.backgroundTexture.image.height;

		console.log("📐 New container aspect:", containerAspect);
		console.log("📐 Image aspect:", imageAspect);

		let planeWidth, planeHeight;

		if (containerAspect > imageAspect) {
			// Container is wider than image - fit to height
			planeHeight = this.config.cameraDistance * 2 * 0.7;
			planeWidth = planeHeight * imageAspect;
		} else {
			// Container is taller than image - fit to width
			planeWidth = this.config.cameraDistance * 2 * 0.7;
			planeHeight = planeWidth / imageAspect;
		}

		console.log("📐 New plane dimensions:", planeWidth, "x", planeHeight);

		// Update geometry
		this.backgroundPlane.geometry.dispose();
		this.backgroundPlane.geometry = new THREE.PlaneGeometry(
			planeWidth,
			planeHeight
		);

		// Recalculate Y offset for background plane to maintain centering
		const frustumHeight =
			2 *
			this.config.cameraDistance *
			Math.tan(THREE.MathUtils.degToRad(40 / 2));
		const frustumWidth = frustumHeight * containerAspect;

		// Position plane to match the logic from createBackgroundPlane()
		// Shift down if plane is taller than frustum to fill bottom gap
		let planeY = 0;
		if (planeHeight > frustumHeight) {
			const excessHeight = planeHeight - frustumHeight;
			// Shift down by more of the excess to ensure bottom coverage after lifting
			// Increased from 60% to 75% to account for the 2.5 unit lift
			planeY = -excessHeight * 0.75;
		}
		// Compensate for camera Y offset - use 90% to balance top and bottom
		planeY -= this.config.cameraYOffset * 0.9;

		// Lift the background image by 3.5 units
		// After lifting, we need to ensure the bottom still covers - adjust positioning
		planeY += 3.5;

		// Additional shift down to ensure bottom coverage after lifting
		// The lift moves the plane up, so we need to compensate to keep bottom covered
		planeY -= 1.0; // Partial compensation to maintain bottom coverage

		this.backgroundPlane.position.y = planeY;

		// Do not scale cylinder here

		console.log("✅ Background plane updated successfully");
	}

	/**
	 * Create a combined texture with front and back labels
	 */
	async createCombinedTexture(typeColor = "#000000") {
		if (!this.sourceCanvas) {
			console.error("No front canvas provided for texture");
			return null;
		}

		console.log(
			"🎨 createCombinedTexture - Source canvas width:",
			this.sourceCanvas.width
		);

		// Create a wide canvas that will hold both front and back labels
		// Keep original width, but condense the content by 50%
		const combinedCanvas = document.createElement("canvas");

		// Add padding to prevent left edge cropping when texture is scaled up
		// Padding of ~15% of source width to account for 0.87 repeat (13% reduction)
		const leftPadding = Math.ceil(this.sourceCanvas.width * 0.15);

		// Width is 2x to accommodate both front (with logo) and back, plus left padding
		combinedCanvas.width = this.sourceCanvas.width * 2 + leftPadding;
		combinedCanvas.height = this.sourceCanvas.height;
		const ctx = combinedCanvas.getContext("2d");

		console.log(
			"🎨 Combined canvas dimensions:",
			combinedCanvas.width,
			"x",
			combinedCanvas.height,
			"(with",
			leftPadding,
			"px left padding)"
		);

		// Condense by 50% (0.5 scale = 50% width)
		const condenseScale = 0.6; // 50% width = 50% condensed
		console.log("🎨 Using condense scale of", condenseScale, "(50% condensed)");

		// Scale factor for front label content (3D preview only) - scales everything up
		// This scales up all text and content, may push logo out of container
		const frontLabelScale = 1.2; // 20% larger for front label only (text will be bigger, logo may be pushed out)
		console.log(
			"🎨 Using front label scale of",
			frontLabelScale,
			"(25% larger for 3D preview - logo may overflow)"
		);

		// Draw front label offset by padding to leave space on the left
		// Create front canvas without logo (logo will be added separately)
		const frontCanvas = await this.createCustomTexture(typeColor, false);
		if (frontCanvas) {
			console.log(
				"🎨 Drawing front canvas with condensing scale:",
				condenseScale,
				"and front label scale:",
				frontLabelScale,
				"(no repetition)"
			);
			// Draw the canvas condensed once - no repetition, offset by padding, with scale up
			// Scale up the front label content for 3D preview
			ctx.save();
			ctx.translate(leftPadding, 0); // Add left padding
			// Scale: horizontal condensing (0.5) + front label scale, vertical front label scale only
			// This makes text/content larger while maintaining horizontal condensing
			ctx.scale(condenseScale * frontLabelScale, frontLabelScale); // Scale up content (X condensed + scaled, Y scaled)
			ctx.drawImage(frontCanvas, 0, 0);
			ctx.restore();

			// Add logo separately at bottom left of front label (3D preview only)
			const customCandleInterface = document.getElementById(
				"custom-candle-interface"
			);
			const collabLogoUrl = customCandleInterface
				? customCandleInterface.getAttribute("data-collab-logo")
				: null;
			const collabLogoTint = customCandleInterface
				? customCandleInterface.getAttribute("data-collab-logo-tint") === "true"
				: false;
			const collabLogoWidth = customCandleInterface
				? Number(customCandleInterface.getAttribute("data-collab-logo-width"))
				: null;
			const collabLogoHeight = customCandleInterface
				? Number(customCandleInterface.getAttribute("data-collab-logo-height"))
				: null;
			const fallbackSvgLogo = document.querySelector("#anecdote-and-svg");
			const logoElement = collabLogoUrl ? collabLogoUrl : fallbackSvgLogo;
			(globalThis.console || console).log("🎨 3D logo source:", {
				collabLogoUrl,
				collabLogoTint,
				collabLogoWidth,
				collabLogoHeight,
				usingLogoType:
					typeof logoElement === "string"
						? "url"
						: logoElement === fallbackSvgLogo
						? "fallback-svg"
						: logoElement
						? "img-element"
						: "none",
			});
			if (logoElement) {
				try {
					await this.drawLogoAtBottomLeft(
						ctx,
						combinedCanvas,
						logoElement,
						typeColor,
						leftPadding,
						condenseScale,
						frontLabelScale,
						collabLogoTint,
						collabLogoWidth,
						collabLogoHeight
					);
				} catch (error) {
					console.warn("Logo render failed; continuing without logo.", error);
				}
			}
		}

		// Draw back label on the right half with condensing
		const condenseScaleBack = condenseScale; // Match front
		if (this.backCanvas) {
			console.log(
				"🎨 Drawing back canvas with condensing scale:",
				condenseScaleBack,
				"(no repetition)"
			);
			// Draw condensed back canvas once - no repetition
			ctx.save();
			ctx.translate(this.sourceCanvas.width + leftPadding, 0); // Move to right half (accounting for padding)
			ctx.scale(condenseScaleBack, 1);
			ctx.drawImage(this.backCanvas, 0, 0);
			ctx.restore();
		} else {
			console.warn("No back canvas found, using front canvas for back");
			if (frontCanvas) {
				console.log(
					"🎨 Drawing front canvas as back with condensing (no repetition)"
				);
				ctx.save();
				ctx.translate(this.sourceCanvas.width, 0);
				ctx.scale(condenseScaleBack, 1);
				ctx.drawImage(frontCanvas, 0, 0);
				ctx.restore();
			}
		}

		console.log(
			"🎨 Combined texture created, final dimensions:",
			combinedCanvas.width,
			"x",
			combinedCanvas.height
		);
		return combinedCanvas;
	}

	/**
	 * Draw logo at bottom left of front label in 3D preview
	 */
	async drawLogoAtBottomLeft(
		ctx,
		combinedCanvas,
		logoElement,
		typeColor,
		leftPadding,
		condenseScale,
		frontLabelScale,
		collabLogoTint = false,
		collabLogoWidth = null,
		collabLogoHeight = null
	) {
		return new Promise((resolve) => {
			const isImageUrl = typeof logoElement === "string" && logoElement.length;

			// Create image from SVG or image URL
			const img = new Image();
			if (isImageUrl) {
				img.crossOrigin = "anonymous";
			}
			let urlToRevoke = null;

			if (isImageUrl) {
				img.src = logoElement;
			} else {
				// Get SVG string from element
				let svgString = logoElement.innerHTML.trim();

				// Replace fill colors with type color
				svgString = svgString.replace(
					/fill="#000000"/gi,
					`fill="${typeColor}"`
				);
				svgString = svgString.replace(
					/fill='#000000'/gi,
					`fill="${typeColor}"`
				);
				svgString = svgString.replace(/fill="#000"/gi, `fill="${typeColor}"`);
				svgString = svgString.replace(/fill='#000'/gi, `fill="${typeColor}"`);

				const svgBlob = new Blob([svgString], {
					type: "image/svg+xml;charset=utf-8",
				});
				urlToRevoke = URL.createObjectURL(svgBlob);
				img.src = urlToRevoke;
			}

			(globalThis.console || console).log("🎨 3D logo load start:", {
				isImageUrl: Boolean(isImageUrl),
				src: img.src,
			});

			img.onload = () => {
				// Logo size - match the 2D preview size
				// Base logo width from source canvas (same as 2D preview)
				const baseLogoWidth = this.sourceCanvas.width * 0.27;

				// Use known SVG dimensions if needed (154 wide x 19 tall)
				const DEFAULT_SVG_WIDTH = 154;
				const DEFAULT_SVG_HEIGHT = 19;

				// Use base logo size with current scale (scale is perfect - keep at 1.15)
				const logoScale = 1.15;

				const srcWidth = img.naturalWidth || DEFAULT_SVG_WIDTH;
				const srcHeight = img.naturalHeight || DEFAULT_SVG_HEIGHT;
				const hasCollabDimensions =
					Number.isFinite(collabLogoWidth) &&
					Number.isFinite(collabLogoHeight) &&
					collabLogoWidth > 0 &&
					collabLogoHeight > 0;
				const imageAspectRatio = srcWidth / srcHeight;
				const collabAspectRatio = hasCollabDimensions
					? collabLogoWidth / collabLogoHeight
					: null;

				// Calculate height first based on desired size
				const defaultLogoHeight =
					(baseLogoWidth * DEFAULT_SVG_HEIGHT) / DEFAULT_SVG_WIDTH;
				const logoHeight = defaultLogoHeight * logoScale * frontLabelScale;

				// Calculate width from height using aspect ratio
				const logoAspectRatio = isImageUrl
					? collabAspectRatio || imageAspectRatio
					: DEFAULT_SVG_WIDTH / DEFAULT_SVG_HEIGHT;
				const logoWidthFromHeight = logoHeight * logoAspectRatio;
				// Match front label horizontal condensing and scale
				const logoWidth = logoWidthFromHeight * condenseScale * frontLabelScale;

				// Position at bottom left of front label area, aligned with text
				// Text starts at 20px from source canvas left edge
				// Calculate where text actually starts in combined canvas after scaling
				const textStartInSource = 20; // Text starts 20px from left in source canvas
				const textStartInCombined =
					leftPadding + textStartInSource * condenseScale * frontLabelScale;

				// Position at bottom left of front label area, aligned with text
				const logoPadding = 0; // prevent slight edge cropping
				const x = textStartInCombined + logoPadding;
				const y = combinedCanvas.height - logoHeight - 48 + logoPadding; // extra bottom padding

				// Verify dimensions - log for debugging
				const drawWidth = Math.max(1, logoWidth - logoPadding * 2);
				const drawHeight = Math.max(1, logoHeight - logoPadding * 2);
				const destAspectRatio = drawWidth / drawHeight;
				(globalThis.console || console).log("🎨 Logo dimensions check:", {
					source: `${srcWidth}×${srcHeight} (ratio: ${imageAspectRatio.toFixed(
						3
					)})`,
					destination: `${drawWidth.toFixed(1)}×${drawHeight.toFixed(
						1
					)} (ratio: ${destAspectRatio.toFixed(3)})`,
					collabMeta: hasCollabDimensions
						? `${collabLogoWidth}×${collabLogoHeight} (ratio: ${collabAspectRatio.toFixed(
								3
						  )})`
						: null,
					note: isImageUrl
						? collabLogoTint
							? "Using image aspect ratio with tint"
							: "Using image aspect ratio (condensed/scaled to label)"
						: "Using SVG aspect ratio (condensed/scaled to label)",
				});

				// Draw using condensed width and fixed height to fix horizontal stretching
				if (isImageUrl && collabLogoTint) {
					const tintCanvas = document.createElement("canvas");
					tintCanvas.width = drawWidth;
					tintCanvas.height = drawHeight;
					const tintCtx = tintCanvas.getContext("2d");
					tintCtx.drawImage(img, 0, 0, drawWidth, drawHeight);
					tintCtx.globalCompositeOperation = "source-in";
					tintCtx.fillStyle = typeColor;
					tintCtx.fillRect(0, 0, logoWidth, logoHeight);
					ctx.drawImage(tintCanvas, x, y);
				} else {
					ctx.drawImage(
						img,
						0,
						0,
						srcWidth,
						srcHeight,
						x,
						y,
						drawWidth,
						drawHeight
					);
				}
				if (urlToRevoke) {
					URL.revokeObjectURL(urlToRevoke);
				}
				(globalThis.console || console).log(
					"✅ Logo drawn at bottom left of front label (3D preview only), properly aligned and not stretched",
					{ collabLogoTint }
				);
				resolve();
			};
			img.onerror = () => {
				if (urlToRevoke) {
					URL.revokeObjectURL(urlToRevoke);
				}
				(globalThis.console || console).warn(
					"Logo image failed to load; skipping logo.",
					{
						isImageElement,
						isImageUrl,
						src: img.src,
					}
				);
				resolve();
			};
		});
	}

	/**
	 * Create a custom texture from the source canvas (without logo for front label in 3D)
	 * Logo will be added separately in the combined texture for 3D preview
	 */
	async createCustomTexture(typeColor = "#000000", includeLogo = false) {
		if (!this.sourceCanvas) {
			console.error("No source canvas provided for texture");
			return null;
		}

		// Create a new canvas for the custom texture
		const customCanvas = document.createElement("canvas");
		customCanvas.width = this.sourceCanvas.width;
		customCanvas.height = this.sourceCanvas.height;
		const ctx = customCanvas.getContext("2d");

		// Draw the original canvas content
		ctx.drawImage(this.sourceCanvas, 0, 0);

		// Only add logo if requested (for back label or other uses)
		if (includeLogo) {
			// Get the SVG from the DOM and draw it
			const svgScript = document.getElementById("anecdote-and-svg");
			if (svgScript) {
				// Replace the fill color in the SVG with the type color
				let svgString = svgScript.innerHTML.trim();

				// Replace all variations of black fill colors
				svgString = svgString.replace(
					/fill="#000000"/gi,
					`fill="${typeColor}"`
				);
				svgString = svgString.replace(
					/fill='#000000'/gi,
					`fill="${typeColor}"`
				);
				svgString = svgString.replace(/fill="#000"/gi, `fill="${typeColor}"`);
				svgString = svgString.replace(/fill='#000'/gi, `fill="${typeColor}"`);

				// Create an image from the SVG and wait for it to load
				const img = new Image();
				const svgBlob = new Blob([svgString], {
					type: "image/svg+xml;charset=utf-8",
				});
				const url = URL.createObjectURL(svgBlob);

				// Wait for the image to load
				await new Promise((resolve, reject) => {
					img.onload = () => {
						// Reduce logo size and raise position to avoid clipping
						const logoWidth = customCanvas.width * 0.27; // reduce ~10% from 0.3
						const logoHeight = (logoWidth * 19) / 154; // maintain aspect ratio
						const x = 20; // left align at 20px (same as text)
						const y = customCanvas.height - logoHeight - 45; // raised from 28px to 45px from bottom

						ctx.drawImage(img, x, y, logoWidth, logoHeight);
						URL.revokeObjectURL(url);
						resolve();
					};
					img.onerror = reject;
					img.src = url;
				});
			} else {
				console.warn("SVG element with id 'anecdote-and-svg' not found");
			}
		}

		return customCanvas;
	}
	async createCylinder() {
		if (!this.sourceCanvas) {
			console.error("No source canvas provided for texture");
			return;
		}

		// Create combined texture with front and back labels
		const combinedCanvas = await this.createCombinedTexture(this.typeColor);
		if (!combinedCanvas) {
			console.error("Failed to create combined texture");
			return;
		}

		// Create texture from combined canvas
		const texture = new THREE.CanvasTexture(combinedCanvas);
		texture.wrapS = THREE.RepeatWrapping; // Wrap around the cylinder
		texture.wrapT = THREE.ClampToEdgeWrapping;

		// Fix 4: Gamma Correction for Textures
		texture.colorSpace = THREE.SRGBColorSpace;

		// Fix orientation - adjust for combined texture
		// Combined texture has padding on left to prevent cropping
		// Scale up texture by reducing repeat - makes texture appear larger on cylinder
		// Repeat of 0.87 means texture covers 87% of UV space, appearing ~15% larger (1/0.87 ≈ 1.15)
		texture.center.set(0.5, 0.5);
		texture.rotation = 0;
		texture.repeat.set(0.87, 0.87); // Scaled up texture (reduced from 1.0 to make it appear larger)
		// Small negative offset to show more of the left side (front label) and account for padding
		texture.offset.set(-0.065, 0); // Shift slightly to show left edge (accounts for padding and 0.87 repeat)

		texture.anisotropy = Math.min(
			16,
			this.renderer.capabilities.getMaxAnisotropy?.() || 8
		);
		texture.minFilter = THREE.LinearMipmapLinearFilter;
		texture.magFilter = THREE.LinearFilter;
		texture.generateMipmaps = true;
		texture.needsUpdate = true;

		// Create cylinder geometry
		const geometry = new THREE.CylinderGeometry(
			this.config.cylinderRadius,
			this.config.cylinderRadius,
			this.config.cylinderHeight,
			this.config.cylinderSegments,
			1,
			true // open ended
		);

		// Adjust UV mapping to properly wrap the label
		this.adjustUVMapping(geometry);

		// Fix 5: Material with proper color management
		// Use clipping plane to hide backside, creating illusion of wrapping around candle
		const material = new THREE.MeshStandardMaterial({
			map: texture,
			side: THREE.DoubleSide,
			roughness: 1.0, // Matte finish
			metalness: 0.0, // No metalness
			transparent: true, // Enable transparency
			opacity: 1.0, // Full opacity for label content
			alphaTest: 0.1, // Hide pixels with low alpha
			// Fix: Ensure proper color handling
			color: new THREE.Color(0xffffff), // White base color
			// Subtle emissive to brighten colors
			emissive: new THREE.Color(0xffffff),
			emissiveIntensity: 0.015, // Very subtle self-illumination
			// Add clipping plane to hide back half - creates wrapping illusion
			clippingPlanes: [
				// Plane that cuts off the back half of the cylinder
				// Camera is at positive Z looking toward negative Z
				// Cylinder center is at Z=0, front (visible) is at negative Z, back (hidden) is at positive Z
				// Normal (0, 0, 1) points in positive Z, clipping everything with Z > 0 (the back)
				new THREE.Plane(new THREE.Vector3(0, 0, 1), 0), // Hide everything with Z > 0 (back side)
			],
		});

		// Create mesh
		if (this.cylinder) {
			this.scene.remove(this.cylinder);
		}

		this.cylinder = new THREE.Mesh(geometry, material);
		this.cylinder.castShadow = true;
		this.cylinder.receiveShadow = true;

		// Calculate initial scale based on container size to match background image
		this.updateCylinderScale();

		// Position the cylinder to align with the candle in the background image (apply scene Y offset)
		this.cylinder.position.set(
			this.config.cylinderXOffset,
			-1 + this.config.cylinderYOffset - this.config.sceneYOffset,
			0
		);

		// Rotate cylinder so the front label faces the camera initially
		// Calculate rotation to center the front label based on texture layout
		// Texture layout: [padding][front label][back label]
		// Front label center is approximately at 18.6% of total texture width
		// (padding 15% + front label half 25% = 40% of source width / 2.15 total = ~18.6%)
		// To center the front label, rotate to bring that UV position to the front
		// Using the config value but calculating it dynamically based on texture layout
		const leftPaddingRatio = 0.15; // 15% padding
		const frontLabelWidthRatio = 0.5; // 50% condensed width
		const totalTextureRatio = 2.0 + leftPaddingRatio; // 2x for front+back + padding
		const frontLabelCenterRatio =
			(leftPaddingRatio + frontLabelWidthRatio / 2) / totalTextureRatio;

		// Rotate to bring front label center to front (UV 0.0)
		// Negative rotation brings left side forward
		// Account for texture offset of -0.065 which shifts texture left
		const rotationToCenter = -frontLabelCenterRatio * Math.PI * 2;
		const offsetCompensation = 0.065 * Math.PI * 2; // Compensate for texture offset

		// Additional 20 degrees counter-clockwise rotation (when looking from above)
		const counterClockwiseAdjustment = THREE.MathUtils.degToRad(-50);

		this.cylinder.rotation.y =
			rotationToCenter + offsetCompensation + counterClockwiseAdjustment;
		// Initialize rotation tracking
		this.objectRotationY = this.cylinder.rotation.y;
		// Store initial rotation for "Front" button
		this.initialRotationY = this.cylinder.rotation.y;

		this.scene.add(this.cylinder);

		// Glass jar effect removed for cleaner look
	}

	/**
	 * Update cylinder scale to match background image scaling
	 * Simple approach: calculate the actual background plane size and use that ratio
	 * This ensures the label scales exactly with the background image at all times
	 */
	updateCylinderScale() {
		if (
			!this.cylinder ||
			!this.initialBackgroundPlaneSize ||
			!this.backgroundTexture
		) {
			return;
		}

		// Get current container dimensions
		const currentWidth = this.container.clientWidth || 1;
		const currentHeight = this.container.clientHeight || 1;
		const containerAspect = currentWidth / currentHeight;

		// Calculate current frustum dimensions (matching camera setup)
		const currentFrustumHeight =
			2 *
			this.config.cameraDistance *
			Math.tan(THREE.MathUtils.degToRad(40 / 2));
		const currentFrustumWidth = currentFrustumHeight * containerAspect;

		// Get background image aspect ratio
		const imageAspect =
			this.backgroundTexture.image.width / this.backgroundTexture.image.height;

		// Calculate current background plane size using the EXACT same logic as createBackgroundPlane()
		// This ensures we're using the same object-fit: cover strategy
		let currentPlaneWidth, currentPlaneHeight;
		if (containerAspect > imageAspect) {
			// Container is wider than image - scale to cover width (crop height)
			currentPlaneWidth = currentFrustumWidth;
			currentPlaneHeight = currentFrustumWidth / imageAspect;
		} else {
			// Container is taller than image - scale to cover height (crop width)
			currentPlaneHeight = currentFrustumHeight;
			currentPlaneWidth = currentFrustumHeight * imageAspect;
		}

		// Calculate scale by comparing current plane size to initial plane size
		// Use the constraining dimension (the one that determines the scale in object-fit: cover)
		let backgroundScale;
		if (containerAspect > imageAspect) {
			// Width is constraining - use width ratio
			backgroundScale =
				currentPlaneWidth / this.initialBackgroundPlaneSize.width;
		} else {
			// Height is constraining - use height ratio
			backgroundScale =
				currentPlaneHeight / this.initialBackgroundPlaneSize.height;
		}

		// Apply scale factor at 100% - label scales exactly with background
		// Then apply global scale adjustment (1.08 = ~19% increase to match lifted background)
		const scaleFactor = backgroundScale * 1.0 * 1.08;

		// Apply scale to cylinder (uniform scaling to maintain proportions)
		this.cylinder.scale.set(scaleFactor, scaleFactor, scaleFactor);

		console.log(
			"🔄 Cylinder scaled to:",
			scaleFactor.toFixed(3),
			"(background scale:",
			backgroundScale.toFixed(3),
			", container:",
			currentWidth,
			"x",
			currentHeight,
			", frustum:",
			currentFrustumWidth.toFixed(2),
			"x",
			currentFrustumHeight.toFixed(2),
			", using object-fit: cover logic matching background plane)"
		);
	}

	/**
	 * Adjust UV mapping to properly wrap the label around the full cylinder
	 * The combined texture is 2x wide (front + back), and we want it to wrap once around
	 */
	adjustUVMapping(geometry) {
		const uv = geometry.attributes.uv;
		const uvArray = uv.array;

		// Adjust UV coordinates to ensure full 360-degree visibility
		// The combined texture is 2x wide, so UV 0-1 should map to the full texture width
		// This ensures front (left half) and back (right half) wrap around the cylinder
		for (let i = 0; i < uvArray.length; i += 2) {
			// Horizontal mapping: map full cylinder circumference (0-1) to full texture width
			// This ensures the 2x wide texture wraps once around the cylinder
			uvArray[i] = uvArray[i];
			// Vertical mapping: use full texture height
			uvArray[i + 1] = uvArray[i + 1];
		}

		uv.needsUpdate = true;
	}

	/**
	 * Add a transparent glass jar overlay for realism
	 */
	addGlassJar() {
		// Create a slightly larger cylinder for the glass effect
		const glassGeometry = new THREE.CylinderGeometry(
			this.config.cylinderRadius + 0.05,
			this.config.cylinderRadius + 0.05,
			this.config.cylinderHeight,
			this.config.cylinderSegments,
			1,
			true
		);

		const glassMaterial = new THREE.MeshPhysicalMaterial({
			color: 0xffffff,
			transparent: true,
			opacity: 0.15,
			roughness: 0.1,
			metalness: 0.0,
			clearcoat: 1.0,
			clearcoatRoughness: 0.1,
			side: THREE.DoubleSide,
		});

		const glassCylinder = new THREE.Mesh(glassGeometry, glassMaterial);
		glassCylinder.castShadow = true;
		glassCylinder.receiveShadow = true;

		this.scene.add(glassCylinder);
	}

	/**
	 * Update the label texture from the canvas
	 * Updates in place to avoid flicker - only replaces texture if it doesn't exist
	 */
	async updateTexture() {
		if (!this.cylinder || !this.sourceCanvas) {
			console.warn("Cannot update texture: cylinder or canvas not initialized");
			return;
		}

		// Check if texture already exists - if so, update it in place to avoid flicker
		const existingTexture = this.cylinder.material.map;

		if (existingTexture && existingTexture instanceof THREE.CanvasTexture) {
			// Create combined texture with front and back labels
			// Do this while the old texture is still visible to avoid flicker
			const combinedCanvas = await this.createCombinedTexture(this.typeColor);
			if (!combinedCanvas) {
				console.warn("Failed to create combined texture for update");
				return;
			}

			// Update existing texture in place - swap the image atomically
			existingTexture.image = combinedCanvas;
			existingTexture.needsUpdate = true;
			// Animation loop will render on next frame - no need for immediate render
			console.log("✅ Texture updated in place (no flicker)");
		} else {
			// Create combined texture with front and back labels
			const combinedCanvas = await this.createCombinedTexture(this.typeColor);
			if (!combinedCanvas) {
				console.warn("Failed to create combined texture for update");
				return;
			}
			// Create new texture only if one doesn't exist
			const texture = new THREE.CanvasTexture(combinedCanvas);
			texture.wrapS = THREE.RepeatWrapping; // Wrap around the cylinder
			texture.wrapT = THREE.ClampToEdgeWrapping;

			// Fix 4: Gamma Correction for Textures
			texture.colorSpace = THREE.SRGBColorSpace;

			// Apply same orientation fixes
			texture.center.set(0.5, 0.5);
			texture.rotation = 0;
			texture.repeat.set(0.87, 0.87); // Scaled up texture (reduced from 1.0 to make it appear larger)
			// Small negative offset to show more of the left side (front label) and account for padding
			texture.offset.set(-0.065, 0); // Shift slightly to show left edge (accounts for padding and 0.87 repeat)

			texture.anisotropy = Math.min(
				16,
				this.renderer.capabilities.getMaxAnisotropy?.() || 8
			);
			texture.minFilter = THREE.LinearMipmapLinearFilter;
			texture.magFilter = THREE.LinearFilter;
			texture.generateMipmaps = true;
			texture.needsUpdate = true;

			this.cylinder.material.map = texture;
			this.cylinder.material.needsUpdate = true;
			console.log("✅ New texture created (first time)");
		}
	}

	/**
	 * Animation loop
	 */
	animate() {
		this.animationId = requestAnimationFrame(() => this.animate());

		// Auto-rotate if enabled - rotate the object, not the camera
		if (this.config.autoRotate && this.cylinder) {
			this.objectRotationY =
				(this.objectRotationY !== undefined
					? this.objectRotationY
					: this.cylinder.rotation.y) +
				this.config.autoRotateSpeed * 0.01;
			this.cylinder.rotation.y = this.objectRotationY;
		}

		// Camera is fixed, no controls to update

		// Render the scene
		this.renderer.render(this.scene, this.camera);
	}

	/**
	 * Setup controls - camera stays fixed, only cylinder rotates
	 */
	setupControls() {
		console.log("🎨 Setting up controls (fixed camera, rotating object)...");

		// Always use custom controls that rotate the object, not the camera
		this.setupObjectRotationControls();
	}

	/**
	 * Setup controls that rotate the object, not the camera
	 * Camera stays fixed in place, only the cylinder rotates
	 * Drag to rotate is disabled - use buttons instead
	 */
	setupObjectRotationControls() {
		// Initialize rotation tracking from current cylinder rotation
		if (this.cylinder && this.objectRotationY === undefined) {
			this.objectRotationY = this.cylinder.rotation.y;
		}

		// Disable drag to rotate - cursor stays default
		this.renderer.domElement.style.cursor = "default";

		console.log(
			"✅ Object rotation controls initialized (drag disabled, use buttons)"
		);
	}

	/**
	 * Set auto-rotate
	 */
	setAutoRotate(enabled) {
		this.config.autoRotate = enabled;
		// No OrbitControls anymore, auto-rotate handled in animate loop
	}

	/**
	 * Set auto-rotate speed
	 */
	setAutoRotateSpeed(speed) {
		this.config.autoRotateSpeed = speed;
		// No OrbitControls anymore, speed handled in animate loop
	}

	/**
	 * Initialize position buttons (Front/Back) - buttons are in Liquid template
	 */
	createPositionButtons() {
		// Find existing buttons from Liquid template
		// Buttons are in #custom-candle-preview__wrapper, not in the 3D container
		const previewWrapper = document.getElementById(
			"custom-candle-preview__wrapper"
		);
		if (!previewWrapper) {
			return;
		}

		const buttonWrapper = previewWrapper.querySelector(
			".three-d-position-buttons-wrapper"
		);
		if (!buttonWrapper) {
			return;
		}

		const frontButton = buttonWrapper.querySelector(
			".three-d-position-button--front"
		);
		const backButton = buttonWrapper.querySelector(
			".three-d-position-button--back"
		);
		const frontButtonSpan = frontButton?.querySelector(
			".three-d-position-button__text"
		);
		const backButtonSpan = backButton?.querySelector(
			".three-d-position-button__text"
		);

		if (!frontButton || !backButton || !frontButtonSpan || !backButtonSpan) {
			return;
		}

		// Track active button (front is active by default from template)
		this.activePositionButton = frontButtonSpan;

		// Add click handlers to update active state
		const setActiveButton = (activeButtonSpan) => {
			// Remove active state from both button spans
			if (frontButtonSpan) {
				frontButtonSpan.classList.remove("is-active");
			}
			if (backButtonSpan) {
				backButtonSpan.classList.remove("is-active");
			}
			// Set active state on clicked button span
			if (activeButtonSpan) {
				activeButtonSpan.classList.add("is-active");
				this.activePositionButton = activeButtonSpan;
			}
		};

		// Add click handlers
		frontButton.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.rotateToFront();
			setActiveButton(frontButtonSpan);
		});

		backButton.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.rotateToBack();
			setActiveButton(backButtonSpan);
		});

		// Ensure container has position relative for absolute positioning
		if (window.getComputedStyle(this.container).position === "static") {
			this.container.style.position = "relative";
		}

		// Store references
		this.positionButtons = {
			wrapper: buttonWrapper,
			front: frontButton,
			back: backButton,
			frontSpan: frontButtonSpan,
			backSpan: backButtonSpan,
		};
	}

	/**
	 * Rotate label to front (default camera position)
	 */
	rotateToFront(animate = true) {
		if (!this.cylinder) {
			console.warn("Cannot rotate to front: cylinder not initialized");
			return;
		}

		const targetRotation = this.initialRotationY;

		if (animate) {
			this.animateRotation(targetRotation);
		} else {
			this.objectRotationY = targetRotation;
			this.cylinder.rotation.y = targetRotation;
		}
	}

	/**
	 * Rotate label to back (180 degrees from front, opposite direction)
	 */
	rotateToBack(animate = true) {
		if (!this.cylinder) {
			console.warn("Cannot rotate to back: cylinder not initialized");
			return;
		}

		const targetRotation = this.initialRotationY - Math.PI; // 180 degrees in opposite direction

		if (animate) {
			this.animateRotation(targetRotation);
		} else {
			this.objectRotationY = targetRotation;
			this.cylinder.rotation.y = targetRotation;
		}
	}

	/**
	 * Animate rotation to target angle
	 */
	animateRotation(targetRotation) {
		if (this.isAnimating) {
			return; // Prevent multiple animations
		}

		this.isAnimating = true;
		const startRotation = this.objectRotationY || this.cylinder.rotation.y;
		const rotationDelta = targetRotation - startRotation;
		const duration = 500; // 500ms animation
		const startTime = performance.now();

		const animate = (currentTime) => {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);

			// Easing function (ease-out)
			const easeOut = 1 - Math.pow(1 - progress, 3);

			// Calculate current rotation
			const currentRotation = startRotation + rotationDelta * easeOut;
			this.objectRotationY = currentRotation;
			this.cylinder.rotation.y = currentRotation;

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				// Animation complete
				this.objectRotationY = targetRotation;
				this.cylinder.rotation.y = targetRotation;
				this.isAnimating = false;
			}
		};

		requestAnimationFrame(animate);
	}

	/**
	 * Reset camera to default position
	 */
	resetCamera() {
		if (this.camera && this.controls) {
			this.camera.position.set(
				0,
				this.config.cameraYOffset,
				this.config.cameraDistance
			);
			this.camera.lookAt(0, 0, 0);
			this.controls.reset();
		}
	}

	/**
	 * Dispose of resources
	 */
	dispose() {
		if (this.animationId) {
			cancelAnimationFrame(this.animationId);
		}

		if (this.controls) {
			this.controls.dispose();
		}

		if (this.backgroundTexture) {
			this.backgroundTexture.dispose();
		}

		if (this.renderer) {
			this.renderer.dispose();
		}

		// Note: Can't remove arrow function event listeners, but that's okay for cleanup
	}
}
